import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";

/**
 * Component that handles Auth0 error callbacks from URL parameters.
 *
 * Auth0 redirects back with: ?error=access_denied&error_description=<code>&state=...
 *
 * Error codes from post-login action:
 * - email_not_verified: User tried to login without verifying email
 * - invitation_not_found: No invitation found for this account (Google login)
 * - validation_failed: Temporary issue with validation (API/linking failure)
 *
 * Error codes from pre-registration action:
 * - email_required: Email address missing from signup request
 * - invitation_required: No invitation exists for this email (password signup)
 * - invitation_validation_failed: Couldn't verify invitation (API failure)
 *
 * Fallback:
 * - Generic "Access Denied" dialog for any unrecognized error
 */
export function Auth0ErrorHandler() {
  const { logout, loginWithRedirect } = useAuth0();
  const { t } = useTranslation(["auth", "common"]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    const errorDescParam = params.get("error_description");

    if (errorParam) {
      console.error("Auth0 error:", errorParam, errorDescParam);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError({ error: errorParam, errorDescription: errorDescParam });
      // Don't clean URL yet - wait for user action
    }
  }, []);

  const handleTryAgain = async () => {
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    setError(null);

    // Logout to clear any cached credentials, then redirect to fresh login
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

    // The returnTo brings them back to origin, then we redirect to login
    setTimeout(() => {
      loginWithRedirect();
    }, 500);
  };

  if (!error) {
    return null;
  }

  // Email not verified error
  if (error.errorDescription === "email_not_verified") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="email-verification-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="email-verification-dialog-title">
          {t("emailVerificationRequired")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DialogContentText>
              {t("emailVerificationMessage")}
            </DialogContentText>

            <Alert severity="info">
              <strong>{t("checkEmailInbox")}</strong>{" "}
              {t("checkEmailInboxMessage")}
            </Alert>

            <DialogContentText variant="body2" color="text.secondary">
              <strong>{t("didntReceiveEmail")}</strong>
              <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
                <li>{t("checkSpamFolder")}</li>
                <li>{t("checkCorrectEmail")}</li>
                <li>{t("verificationLinkExpiry")}</li>
              </ul>
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTryAgain}
            variant="contained"
            autoFocus
            fullWidth
          >
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Invitation missing (login)
  if (error.errorDescription === "invitation_not_found") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="invitation-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="invitation-dialog-title">
          {t("invitationRequired")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t("invitationNotFound")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTryAgain} variant="contained" fullWidth>
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Invitation required (signup)
  if (error.errorDescription === "invitation_required") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="invitation-required-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="invitation-required-dialog-title">
          {t("invitationRequired")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DialogContentText>{t("inviteOnlyMessage")}</DialogContentText>

            <Alert severity="info">
              <strong>{t("dontHaveInvitation")}</strong>
              <br />
              {t("contactAdminForAccess")}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTryAgain} variant="contained" fullWidth>
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Invitation validation failed (signup)
  if (error.errorDescription === "invitation_validation_failed") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="invitation-validation-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="invitation-validation-dialog-title">
          {t("unableToVerifyInvitation")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <DialogContentText>
              {t("invitationValidationIssue")}
            </DialogContentText>

            <Alert severity="warning">{t("tryAgainMoments")}</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTryAgain} variant="contained" fullWidth>
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Email required (signup - should rarely happen)
  if (error.errorDescription === "email_required") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="email-required-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="email-required-dialog-title">
          {t("emailRequired")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t("emailRequiredMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTryAgain} variant="contained" fullWidth>
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Generic validation failure
  if (error.errorDescription === "validation_failed") {
    return (
      <Dialog
        open={true}
        onClose={handleTryAgain}
        aria-labelledby="validation-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="validation-dialog-title">
          {t("temporaryIssue")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t("validationFailedMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTryAgain} variant="contained" fullWidth>
            {t("tryAgain", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Generic access denied error
  return (
    <Dialog
      open={true}
      onClose={handleTryAgain}
      aria-labelledby="access-denied-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="access-denied-dialog-title">
        {t("accessDenied")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DialogContentText>{t("accessDeniedMessage")}</DialogContentText>

          <Alert severity="error">
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>{t("emailVerificationRequiredShort")}</li>
              <li>{t("noValidInvitation")}</li>
              <li>{t("accountAccessRestrictions")}</li>
            </ul>
          </Alert>

          <DialogContentText variant="body2">
            {t("contactSupportMessage")}
          </DialogContentText>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleTryAgain} variant="contained" fullWidth>
          {t("tryAgain", { ns: "common" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
